import * as React from 'react';
import { Form } from 'react-bootstrap';
import { Dropdown, Editable, File, Richtext, useCacheRecord } from 'gossamer-front-end';
import { InstitutionView } from 'cb-model';
import { EventTypeOptions } from 'cb-model/SharedOptions';
import { FieldType, FileStorageType } from 'gossamer-unified';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, EventErrors, HandleEventDetailsChange, ValidateEventFormField } from './eventDetailsFormTypes';

type Props<T extends EventDetails> = {
  eventDetails: T | undefined;
  handleDetailsChange: HandleEventDetailsChange<T>;
  errors: EventErrors;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  institutionId: number;
  validateForm: ValidateEventFormField<T>;
};

const validityFromError = (err?: string) => (err ? false : undefined);

const TITLE_FIELD = { seq: 10, type: FieldType.Text, label: 'Event title', mandatory: true };

export function EventBasicDetailsSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, handleDetailsChange, errors, setErrors, institutionId, validateForm } = props;

  const isPersonalInstitution = institutionId === PERSONAL_INSTITUTION_ID;

  const institutionRecord = useCacheRecord(
    InstitutionView,
    String(institutionId),
    !institutionId || isPersonalInstitution,
  );

  const typeOptions = React.useMemo(
    () => EventTypeOptions.filter((o) => o.active !== false).map((o) => ({ id: o.id, label: o.label })),
    [],
  );

  return (
    <EventDetailsSectionLayout
      title="Basic details"
      subtitle="Please provide key information regarding your event. The information must be accurate, specific and complete."
    >
      <Form.Group controlId="event-title">
        <Form.Label>Event title*</Form.Label>
        <Editable
          field={TITLE_FIELD as any}
          value={String(eventDetails?.title || '')}
          placeholder="My training session"
          validity={validityFromError(errors.title)}
          handleChange={(val) => handleDetailsChange('title' as keyof T, val)}
          handleBlur={() => validateForm({ eventDetails, setErrors, key: 'title' as keyof T })}
        />
        {errors.title && <Form.Text className="text-danger">{errors.title}</Form.Text>}
      </Form.Group>

      <Form.Group controlId="event-type">
        <Form.Label>Event type*</Form.Label>
        <Dropdown
          mandatory={true}
          onItemSelected={(id) => {
            handleDetailsChange('type' as keyof T, id);
            validateForm({ eventDetails, setErrors, key: 'type' as keyof T });
          }}
          options={typeOptions}
          validity={validityFromError(errors.type)}
          value={String(eventDetails?.type || '')}
        />
        {errors.type && <Form.Text className="text-danger">{errors.type}</Form.Text>}
      </Form.Group>

      <Form.Group controlId="event-markdown-description">
        <Form.Label>Description*</Form.Label>
        <Richtext
          mandatory={true}
          value={String((eventDetails as any)?.markdown_description || '')}
          validity={validityFromError(errors.markdown_description)}
          handleChange={(text) => {
            handleDetailsChange('markdown_description' as keyof T, text);
            validateForm({ eventDetails, setErrors, key: 'markdown_description' as keyof T });
          }}
        />
        {errors.markdown_description && <Form.Text className="text-danger">{errors.markdown_description}</Form.Text>}
      </Form.Group>

      {!isPersonalInstitution && !!institutionRecord.data && (
        <div>
          This event is being created on behalf of <b>{(institutionRecord.data as any).name}</b>
        </div>
      )}
      {isPersonalInstitution && <div>This event is being created as a personal event on your behalf</div>}

      <Form.Group controlId="event-image">
        <Form.Label>Event image</Form.Label>
        <File
          editable={true}
          mandatory={false}
          imagesOnly={true}
          storageType={FileStorageType.ExternalResource}
          storeMimeTypeAndTitle={false}
          value={String((eventDetails as any)?.image || '')}
          handleChange={(value) => handleDetailsChange('image' as keyof T, value || '')}
        />
      </Form.Group>
    </EventDetailsSectionLayout>
  );
}
